import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import CoreFrameDropDownTreeViewViewModel from '@/ViewModels/Controls/CoreFrameDropDownTreeViewViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { IDropDownTreeViewViewModelConstructorParameter, NodeData } from '#/DefaultViewExtensionInterfaces';
import TreeViewItemViewModel from '@/ViewModels/Controls/TreeViewItemViewModel';
import Utilities from "@/Utility/Utilities";
import DropDownTreeViewItemViewModel from "@/ViewModels/Controls/DropDownTreeViewItemViewModel";

/**
 * Das ViewModel für die DropDownTreeView
 */
export default class DropDownTreeViewViewModel extends ControlViewModel {
    private notifyHandlers: boolean;

    private readonly dataSource: KnockoutObservableArray<NodeData>;
    private allItems: Array<any>;

    /**
     * Enthält die Kindelemente
     */
    public items: KnockoutObservableArray<TreeViewItemViewModel | DropDownTreeViewItemViewModel>;

    /**
     * Gibt an, ob Checkboxen angezeigt werden sollen
     */
    public hasCheckboxes: KnockoutObservable<boolean>;

    /**
     * Gibt eine konvertierte Liste der Items zurück
     */
    public sortedItems: KnockoutComputed<Array<TreeViewItemViewModel| DropDownTreeViewItemViewModel>>;

    /**
     * Die selektierten Werte
     */
    public values: KnockoutObservableArray<string>;

    /**
     * Enthält ein Array mit Arrays als Elementen.
     * Jedes Element enthält auf dem Index '0' die pdoId gefolgt von weiteren zugehörigen Datenfeldern (z.b. objectIdentValue).
     */
    public fullValues: KnockoutObservableArray<Array<any>>;

    /**
     * Das ViewModel für das Error-Label
     */
    public label: LabelViewModel;

    /**
     * Enthält alle Knoten des Baumes, die Teil eines Zyklus' sind.
     */
    public circularities: Array<any>;

    /**
     * Callback function that executes when the CheckBoxViewModel of a Node changes from Unchecked to Checked.
     */
    public onChecked(value: string | boolean): void {}

    /**
     * Callback function that executes when the CheckBoxViewModel of a Node changes from Checked to Unchecked.
     */
    public onUnchecked(value: string | boolean): void {}

    /**
     * CoreFrameDropDownTreeViewViewModel (replaces Kendo-TreeView)
     */
    public coreFrameDropDownTreeViewViewModel: CoreFrameDropDownTreeViewViewModel;

    /**
     * Callback, welches aufgerufen wird, wenn das DropDown geöffnet wird
     */
    public onOpenCallback: () => unknown | JQueryPromise<unknown>;

    /**
     * Callback, welches aufgerufen wird, wenn das DropDown geschlossen wird
     */
    public onCloseCallback: () => unknown | JQueryPromise<unknown>;

    /**
     * Gibt an, ob die Daten innerhalb des DropDowns lazy geladen werden sollen
     */
    public lazyLoading: boolean;
    
    /**
     * Das ViewModel für die DropDownTreeView
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params: IDropDownTreeViewViewModelConstructorParameter) {
        super(params);

        this.notifyHandlers = true;

        this.lazyLoading = this.paramOrDefault(params.lazyLoading, false);
        this.items = this.observableOrDefault(params.items, []) as KnockoutObservableArray<TreeViewItemViewModel>;
        this.hasCheckboxes = this.observableOrDefault(params.hasCheckboxes, false);

        // updates the trees datasource when 'sortedItems' changes.
        this.sortedItems = this.defineComputed(() => {
            let items = this.items();

            let sortedItems = _.sortBy(items, (treeViewItem: TreeViewItemViewModel) => {
                return treeViewItem.link.label.caption();
            });

            return sortedItems;
        });

        this.onOpenCallback = this.handlerOrDefault(params.onOpen, () => { return Utilities.resolveImmediately() });
        this.onCloseCallback = this.handlerOrDefault(params.onClose);
        
        this.values = this.observableOrDefault(params.values, []) as KnockoutObservableArray<string>;
        this.fullValues = this.observableOrDefault(params.fullValues, []) as KnockoutObservableArray<any>;
        this.label = this.paramOrDefault(params.label, null);
        this.circularities = [];
        this.onChecked = (value: string) => {
            if (params.onChecked && this.notifyHandlers)
                params.onChecked(value);
        };

        this.onUnchecked = (value: string) => {
            if (params.onUnchecked && this.notifyHandlers)
                params.onUnchecked(value);
        };

        // ### integration of CoreFrameDropDownTreeView

        this.dataSource = this.observableOrDefault([]) as KnockoutObservableArray<NodeData>;
        this.allItems = [];

        this.defineSubscription(this.sortedItems, (_items: any) => {
            this.allItems = [];
            this.dataSource(this.convertItemsToNodes(_items));
        })(this.sortedItems());

        // creates the CoreFrameDropDownTreeViewViewModel as an inner ViewModel
        this.coreFrameDropDownTreeViewViewModel = new CoreFrameDropDownTreeViewViewModel({
            hasCheckboxes: true,
            isMultiselect: true,
            selectedKeys: this.values,
            fullValues: this.fullValues,
            dataSource: this.dataSource,
            isBusy: this.isBusy,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            invalidationText: this.invalidationText,
            errorLabel: this.label,
            onUnchecked: this.onUnchecked,
            onChecked: this.onChecked,
            parentLocator: this.parentLocator,
            locator: this.locator,
            tooltip: this.tooltip,
            onOpenCallback: this.onOpenCallback,
            onClosedCallback: this.onCloseCallback,
            lazyLoading: this.lazyLoading
        });

        // registering callbacks for when 'values' changes
        // this happens initially when values are loaded (change from empty to non-empty values)
        this.defineSubscription(this.values, (_values: any) => {
            // update selection and selectionTooltip dependent on any given circularities
            let itemLabelsOfCyclicValues = this.circularities
                .filter((anyItem: any) => { return _.contains(_values, anyItem.value()) })
                .map((anyItem: any) => { return anyItem.link.label.caption() });

            if (!_.isEmpty(itemLabelsOfCyclicValues)) {
                this.coreFrameDropDownTreeViewViewModel.selection(this.coreFrameDropDownTreeViewViewModel.selection() + " [" + itemLabelsOfCyclicValues.join('; ') + "]");
                this.coreFrameDropDownTreeViewViewModel.selectionTooltip(this.coreFrameDropDownTreeViewViewModel.selectionTooltip() + " \r\n\r\n" + itemLabelsOfCyclicValues.join('\r\n'));
            }

            // set nodes to checked or unchecked without triggering the registered check- or uncheck-callbacks
            this.notifyHandlers = false;
            this.allItems.forEach((anyItem: any) => {
                let calculatedPdoIdSelection = _values;
                let pdoId = anyItem.value();
                let node = this.coreFrameDropDownTreeViewViewModel.getNodeByKey(pdoId);

                if (_.contains(calculatedPdoIdSelection, pdoId)) {
                    if (!node.checkBoxViewModel.isChecked()) {
                        node.checkBoxViewModel.isChecked(true);
                    }
                }
                else {
                    if (node.checkBoxViewModel.isChecked()) {
                        node.checkBoxViewModel.isChecked(false);
                    }
                }
            });
            this.notifyHandlers = true;
        });
        // ###
    }

    private convertItemsToNodes(_items: any): any {
        return _items.map((_item: any) => {
            this.allItems.push(_item);

            return {
                key: _item.value(),
                title: _item.link.label.caption(),
                children: this.convertItemsToNodes(_item.items ? _item.items() : []),
                isCheckable: _item.isCheckable,
                isExpandedObs: _item.isExpanded,
                locator: _item.locator
            };
        });
    }
}

ComponentManager.setViewModel('DropDownTreeViewViewModel', DropDownTreeViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');